import type { TableSortEvent } from '@app/shared/vue/components/sf-table/types'
import type { QueryParam } from '@app/application/user-management/utils/list-query'
import type {
  SortableUserColumn,
  UserLastLoginFilter,
  UserList,
  UserRole,
  UserRow,
  UserSortDirection,
  UserStatus,
} from '@app/application/user-management/types'

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import {
  isRoleOfList,
  isSortableUserColumn,
  isUserLastLoginFilter,
  isUserRole,
  isUserStatus,
} from '@app/application/user-management/predicates'
import {
  userColumns,
  userLists,
  userSortDirections,
} from '@app/application/user-management/types'
import {
  FIRST_PAGE,
  QUERY_KEYS,
  STALE_USERS_REQUEST_REASON,
  USERS_REQUEST_KEY,
} from '@app/application/user-management/constants'
import {
  startLatestRequest,
  toPage,
  toSearch,
  toSortDirection,
} from '@app/application/user-management/utils/list-query'
import { fetchUsers } from '@app/application/user-management/api'
import { SfLog } from '@app/adapters/logger'
import {
  createListHistory,
  urlField,
} from '@app/application/user-management/utils/list-history'

const toSortBy = (param: QueryParam): SortableUserColumn =>
  typeof param === 'string' && isSortableUserColumn(param)
    ? param
    : userColumns.NAME

const toRole = (param: QueryParam): UserRole | undefined =>
  typeof param === 'string' && isUserRole(param) ? param : undefined

const toStatus = (param: QueryParam): UserStatus | undefined =>
  typeof param === 'string' && isUserStatus(param) ? param : undefined

const toLastLogin = (param: QueryParam): UserLastLoginFilter | undefined =>
  typeof param === 'string' && isUserLastLoginFilter(param) ? param : undefined

// A filter with no value leaves the query string, which is how "All" is
// expressed: the absence of the filter rather than a value of its own.
const toParam = (value: string | undefined): string => value ?? ''

// The list is one page with one table on it, so a store holds what the table,
// the toolbar and the empty state all read.
export const useUsersListStore = defineStore('usersList', () => {
  const list = ref<UserList>(userLists.STAFF)

  const sortBy = ref<SortableUserColumn>(userColumns.NAME)
  const sortDirection = ref<UserSortDirection>(userSortDirections.ASC)
  const page = ref(FIRST_PAGE)
  const search = ref<string | undefined>()
  const role = ref<UserRole | undefined>()
  const status = ref<UserStatus | undefined>()
  const lastLogin = ref<UserLastLoginFilter | undefined>()

  const listHistory = createListHistory([
    urlField({
      key: QUERY_KEYS.SORT_BY,
      value: sortBy,
      initial: userColumns.NAME,
      serialize: String,
      deserialize: toSortBy,
    }),
    urlField({
      key: QUERY_KEYS.SORT_DIRECTION,
      value: sortDirection,
      initial: userSortDirections.ASC,
      serialize: String,
      deserialize: toSortDirection,
    }),
    urlField({
      key: QUERY_KEYS.PAGE,
      value: page,
      initial: FIRST_PAGE,
      serialize: String,
      deserialize: toPage,
    }),
    urlField({
      key: QUERY_KEYS.SEARCH,
      value: search,
      initial: undefined,
      serialize: toParam,
      deserialize: toSearch,
    }),
    urlField({
      key: QUERY_KEYS.ROLE,
      value: role,
      initial: undefined,
      serialize: toParam,
      deserialize: toRole,
    }),
    urlField({
      key: QUERY_KEYS.STATUS,
      value: status,
      initial: undefined,
      serialize: toParam,
      deserialize: toStatus,
    }),
    urlField({
      key: QUERY_KEYS.LAST_LOGIN,
      value: lastLogin,
      initial: undefined,
      serialize: toParam,
      deserialize: toLastLogin,
    }),
  ])

  const users = ref<UserRow[]>([])
  const totalPages = ref(FIRST_PAGE)
  const loading = ref(true)
  const error = ref(false)

  const isFiltered = computed(() =>
    Boolean(search.value || role.value || status.value || lastLogin.value)
  )

  const setSort = (sort: TableSortEvent): void => {
    if (!isSortableUserColumn(sort.sortBy)) {
      return
    }

    sortBy.value = sort.sortBy
    sortDirection.value = toSortDirection(sort.sortDirection)
    // The rows the old page held are not the rows this sort puts there.
    page.value = FIRST_PAGE
    listHistory.push()
  }

  const setPage = (next: number): void => {
    page.value = next
    listHistory.push()
  }

  // A filter answers with a list of its own, so the page the reader was on says
  // nothing about the one they are now looking at.
  const setSearch = (term: string): void => {
    search.value = term.trim() || undefined
    page.value = FIRST_PAGE
    listHistory.pushSearch()
  }

  const setRole = (value: string): void => {
    role.value = isRoleOfList(value, list.value) ? value : undefined
    page.value = FIRST_PAGE
    listHistory.push()
  }

  const setStatus = (value: string): void => {
    status.value = isUserStatus(value) ? value : undefined
    page.value = FIRST_PAGE
    listHistory.push()
  }

  const setLastLogin = (value: string): void => {
    lastLogin.value = isUserLastLoginFilter(value) ? value : undefined
    page.value = FIRST_PAGE
    listHistory.push()
  }

  const clearFilters = (): void => {
    search.value = undefined
    role.value = undefined
    status.value = undefined
    lastLogin.value = undefined
    page.value = FIRST_PAGE
    listHistory.push()
  }

  const loadUsers = async (): Promise<void> => {
    const isStale = startLatestRequest(
      USERS_REQUEST_KEY,
      STALE_USERS_REQUEST_REASON
    )

    loading.value = true
    error.value = false

    try {
      const response = await fetchUsers({
        list: list.value,
        page: page.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
        search: search.value,
        role: role.value,
        status: status.value,
        lastLogin: lastLogin.value,
      })

      if (isStale()) {
        return
      }

      users.value = response?.users ?? []
      totalPages.value = response?.pagination?.totalPages ?? FIRST_PAGE

      // A bookmarked page past the end answers with no rows and the page it was
      // asked for, so the list holds on to the last page it does have.
      if (page.value > totalPages.value) {
        page.value = totalPages.value
        listHistory.replace()
      }
    } catch (e) {
      if (isStale()) {
        return
      }

      SfLog.warn('Failed to load the users list', { extra: { error: e } })

      // The page count stays as it was, so the pager is still there to retry with.
      error.value = true
    } finally {
      if (!isStale()) {
        loading.value = false
      }
    }
  }

  const initialize = (target: UserList): void => {
    list.value = target

    // The URL is read before the list is known, so it can still hold the role of
    // the other one. The server ignores it and the trigger has no name for it.
    if (role.value && !isRoleOfList(role.value, target)) {
      role.value = undefined
      listHistory.replace()

      // Clearing it wakes the watcher, which loads once.
      return
    }

    void loadUsers()
  }

  watch(
    [sortBy, sortDirection, page, search, role, status, lastLogin],
    loadUsers
  )

  // Everything readable leaves as a computed and everything writable as an
  // action, so no consumer can put the store somewhere loadUsers would not.
  return {
    clearFilters,
    error: computed(() => error.value),
    initialize,
    isFiltered,
    lastLogin: computed(() => lastLogin.value),
    list: computed(() => list.value),
    loading: computed(() => loading.value),
    loadUsers,
    page: computed(() => page.value),
    role: computed(() => role.value),
    search: computed(() => search.value),
    setLastLogin,
    setPage,
    setRole,
    setSearch,
    setSort,
    setStatus,
    sortBy: computed(() => sortBy.value),
    sortDirection: computed(() => sortDirection.value),
    status: computed(() => status.value),
    totalPages: computed(() => totalPages.value),
    users: computed(() => users.value),
  }
})
